// PendingConfirmationsModal — Feb 2026 pt89h (Randy). Parent-actors approve
// / reject kids' proposed chore completions from within the app. No PIN
// required (parent is already authenticated in-app; PIN is a wall-tablet
// gate only). Approve calls /calendar/events/{id}/approve-done; Reject
// calls /reject-done. Toast + refetch on success. If the list empties,
// closes itself.
import { useCallback, useEffect, useState } from "react";
import { X, Check, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PendingConfirmationsModal({ siloId, siloName, onClose, onCountChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/pending-confirmations`);
      setRows(r.data?.pending || []);
      onCountChanged?.(r.data?.count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load pending confirmations.");
    } finally { setLoading(false); }
  }, [siloId, onCountChanged]);
  useEffect(() => { load(); }, [load]);

  const act = async (row, action) => {
    setBusyId(row.event_id + row.date + action);
    try {
      // Feb 26, 2026 (Randy pt-kid-propose-alert-gap) — proposals
      // (kid-suggested new items) approve/decline via a different
      // endpoint than completions. Route based on row.kind.
      const isProposal = row.kind === "proposal";
      const url = isProposal
        ? (action === "approve"
            ? `/calendar/events/${row.event_id}/approve`
            : `/calendar/events/${row.event_id}/decline`)
        : (action === "approve"
            ? `/calendar/events/${row.event_id}/approve-done`
            : `/calendar/events/${row.event_id}/reject-done`);
      await api.post(url, isProposal ? {} : { date: row.date });
      if (isProposal) {
        toast.success(action === "approve"
          ? `Added "${row.title}" to the calendar.`
          : `Declined "${row.title}".`);
      } else {
        toast.success(action === "approve" ? `Approved ${row.title}.` : `Marked ${row.title} incomplete.`);
      }
      // Optimistically drop this row; the modal will close if it was the last one.
      setRows((prev) => {
        const next = prev.filter((x) => !(x.event_id === row.event_id && x.date === row.date));
        onCountChanged?.(next.length);
        if (next.length === 0) setTimeout(onClose, 250);
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update.");
    } finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="pending-confirmations-modal">
      <div className="bg-white border hairline rounded-2xl w-full max-w-md max-h-[80dvh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-5 py-4 border-b hairline">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Awaiting your OK</div>
            <div className="font-serif-display text-lg truncate text-[#1C1B1A]">{siloName || "Family"}</div>
          </div>
          <button onClick={onClose} data-testid="pending-confirmations-close"
            className="p-1.5 rounded text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE]">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="text-xs text-[#6B6A68] flex items-center gap-2 py-8 justify-center" data-testid="pending-confirmations-loading">
              <Spinner size={12} className="animate-spin" /> Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[#6B6A68] italic text-center py-8" data-testid="pending-confirmations-empty">
              Nothing to approve right now.
            </div>
          )}
          <ul className="space-y-2">
            {rows.map((row) => {
              const rowKey = row.event_id + row.date;
              const isProposal = row.kind === "proposal";
              return (
                <li key={rowKey} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#FBFAF6] border hairline"
                  data-testid={`pending-row-${row.event_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1C1B1A] truncate">
                      <span className="font-medium">{row.assignee_name}</span>
                      <span className="text-[#6B6A68]"> · {row.title}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mt-0.5 flex items-center gap-1.5">
                      {isProposal ? (
                        <span data-testid={`pending-proposal-tag-${row.event_id}`}
                          className="px-1.5 py-0.5 rounded bg-[#F8F1E2] border border-[#C19A4A] text-[#8A6A24] tracking-[0.14em]">
                          New {row.event_kind || "item"}
                        </span>
                      ) : (
                        <span>{new Date(row.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => act(row, "reject")} disabled={busyId?.startsWith(rowKey)}
                    data-testid={`pending-reject-${row.event_id}`}
                    className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40">
                    {isProposal ? "Decline" : "Reject"}
                  </button>
                  <button onClick={() => act(row, "approve")} disabled={busyId?.startsWith(rowKey)}
                    data-testid={`pending-approve-${row.event_id}`}
                    className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md bg-[#2C6E92] text-white hover:bg-[#245878] flex items-center gap-1 disabled:opacity-40">
                    <Check size={11} weight="bold" /> {isProposal ? "Add it" : "Approve"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
